import { activeSiteConfig } from "@config/site/site-config";
import { Loader as GoogleMaps } from "@googlemaps/js-api-loader";
import { MarkerClusterer } from "@googlemaps/markerclusterer";
import type { ProductVariantAvailabilityWithChannel } from "@graphql/generated/components";
import { LogTag, Logger, ServiceType } from "@lib/monitoring/logger";
import { IconButton } from "@ui/components/forms/icon-button/IconButton";
import { Box } from "@ui/components/layout/box/Box";
import { Image } from "@ui/components/media-and-icons/image/Image";
import { space } from "@ui/design-tokens";
import { isEmpty, isNumber } from "lodash-es";
import { useRouter } from "next/router";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { createRoot } from "react-dom/client";
import { StoresToggle } from "./components/stores-toggle/StoresToggle";
import type { StoreData } from "./controller";
import { Controller } from "./controller";
import { icon } from "./icons";
import { createMarker, storeMarkerIcon } from "./map-markers";
import { clusterAlgorithm, clusterRenderer } from "./map-options";
import { calculateZoomLevel } from "./utils/calculateZoomLevel";
import { isEccoStore } from "./utils/isEccoStore";
import { getMarkersOnMap } from "./utils/getMarkersOnMap";
import { MAP_CONTAINER } from "@e2e/helpers/store-locator/constants/common";
import { findNearestPoint } from "./utils/findNearestGeoPoint";

type Markers = {
    [storeId: string]: google.maps.Marker;
};

enum MapState {
    Unmounted,
    Mounted,
    Ready,
    Completed,
}

const MAP_CLUSTER_ZOOM = 12;

interface StoreLocatorProps {
    storesWithStock?: ProductVariantAvailabilityWithChannel[] | StoreData[];
    isExtraLargeViewport?: boolean;
}

let map: google.maps.Map;

export const StoreLocatorMap = ({ storesWithStock, isExtraLargeViewport }: StoreLocatorProps) => {
    const { locale } = useRouter();

    const [mergedStores, setMergedStores] = useState<StoreData[]>([]);
    const [idle, setIdle] = useState<number>(0);
    const [markers, setMarkers] = useState<Markers>({});
    const [markerClusterer, setMarkerClusterer] = useState<MarkerClusterer | null>(null);
    const [clientMarker, setClientMarker] = useState<google.maps.Marker>();
    const [, setSelectedMarker] = useState<string>("");
    const zoomOnSelect = 15;

    const {
        selectedStoreId,
        setSelectedStoreId,
        setHighlightSelectedStore,
        storeData,
        setStoreData,
        clientLocation,
        setVisibleStores,
        setLastSelect,
        searchResult,
        setMapBounds,
        mapBounds,
        setStoreDataIndex,
        setIsClickAndCollect,
        showPartnerStores,
        getShouldShowStore,
        mapState,
        storeDataIndex,
        setMapState,
        isClickAndCollect,
        setIsLoading,
        filterDistance,
        setFilterDistance,
    } = useContext(Controller);

    // This will be triggered only when storesWithStock is defined, this means is CC Store Locator
    const storeWithStocksDictionary = useMemo(() => {
        if (!storesWithStock) return {};
        const dictionary = {};
        storesWithStock?.forEach((item) => {
            dictionary[item?.supplyChannel?.[0]?.id ?? item?.channel?.id] = item;
        });

        return dictionary;
    }, [storesWithStock]);

    // This will be triggered only when storesWithStock is defined, this means is CC Store Locator
    useEffect(() => {
        if (!storesWithStock) return;

        setIsClickAndCollect(true);
        setIsLoading(true);
        const mergedStores = storeData.map((storeDataItem) => {
            const storeWithStocks = storeWithStocksDictionary[storeDataItem.supplyChannel?.[0].id];
            if (storeWithStocks) {
                return {
                    ...storeDataItem,
                    availability: storeWithStocks.availability,
                };
            }
        });

        setMergedStores(mergedStores);
    }, [
        storesWithStock,
        storeWithStocksDictionary,
        setStoreDataIndex,
        setStoreData,
        storeData,
        setIsClickAndCollect,
    ]);

    // This will be triggered only when storesWithStock is defined, this means is CC Store Locator
    useEffect(() => {
        if (markerClusterer && mapState === MapState.Ready) {
            const handleSelectedStore = (storeId: string) => {
                setSelectedStoreId(storeId);
                setLastSelect(Date.now());
            };

            const markerCollection = (
                isEmpty(mergedStores) ? storeData : mergedStores
            ).reduce<Markers>((collection, store) => {
                collection[store?.storeId] = createMarker({
                    icon: isEccoStore(store)
                        ? storeMarkerIcon(icon.storePin.src)
                        : storeMarkerIcon(icon.partnerPin.src),
                    position: new google.maps.LatLng(store?.latitude, store?.longitude),
                    title: store?.name,
                    zIndex: 1,
                    onClick: () => {
                        map.setValues({
                            ...(map.getZoom() < zoomOnSelect && { zoom: zoomOnSelect - 1 }),
                            center: new google.maps.LatLng(store?.latitude, store?.longitude),
                        });
                        handleSelectedStore(store?.storeId);
                    },
                    visible: getShouldShowStore(store.storeId),
                });
                return collection;
            }, {});

            const markersOnMap = getMarkersOnMap(markerCollection, map);

            markerClusterer.clearMarkers();
            markerClusterer.addMarkers(Object.values(markersOnMap));

            if (clientMarker) {
                markerClusterer.addMarker(clientMarker);
                map.setValues({
                    center: clientMarker.getPosition(),
                });
            }

            setMarkers(markerCollection ?? {});
            setMapState(MapState.Completed);
            if (isClickAndCollect) {
                setIsLoading(false);
            }
        } else if (mapState === MapState.Completed) {
            setSelectedMarker((prevSelectedStoreId) => {
                if (prevSelectedStoreId !== selectedStoreId) {
                    markers[prevSelectedStoreId]?.setIcon(
                        isEccoStore(storeDataIndex[prevSelectedStoreId])
                            ? storeMarkerIcon(icon.storePin.src)
                            : storeMarkerIcon(icon.partnerPin.src)
                    );
                    markers[prevSelectedStoreId]?.setZIndex(1);
                }

                if (selectedStoreId !== "") {
                    markers[selectedStoreId]?.setIcon(storeMarkerIcon(icon.storePinSelected.src));
                    markers[selectedStoreId]?.setZIndex(2);
                }

                return selectedStoreId;
            });

            if (selectedStoreId !== "" && mapState === MapState.Completed) {
                setHighlightSelectedStore((prevState) => {
                    if (prevState) {
                        if (map && map?.getZoom() < zoomOnSelect) {
                            map?.panTo(
                                markers?.[selectedStoreId]?.getPosition() ||
                                    clientMarker?.getPosition() ||
                                    mapBounds?.getCenter()
                            );
                            map?.setZoom(zoomOnSelect);
                        }
                        return true;
                    }
                    return false;
                });
            }
        }
    }, [
        mapState,
        storeData,
        markers,
        selectedStoreId,
        clientMarker,
        setSelectedStoreId,
        setLastSelect,
        setHighlightSelectedStore,
        mergedStores,
        markerClusterer,
    ]);

    useEffect(() => {
        setStoreDataIndex(
            mergedStores.reduce((index, store) => {
                index[store?.storeId] = store;
                return index;
            }, {})
        );
    }, [mergedStores, setStoreDataIndex]);

    useEffect(() => {
        if (mapState === MapState.Mounted && (storeData.length > 0 || storesWithStock)) {
            setMapState(MapState.Ready);
        }
    }, [mapState, storeData]);

    useEffect(() => {
        if (map && mapState === MapState.Completed) {
            const center = map?.getCenter();
            const bounds = map?.getBounds();

            const ne = bounds?.getNorthEast();
            const sw = bounds?.getSouthWest();

            const lat = center?.lat();
            const lng = center?.lng();

            const latLow = lat - (lat - sw?.lat());
            const latHigh = lat + (ne?.lat() - lat);
            const lngLow = lng - (lng - sw?.lng());
            const lngHigh = lng + (ne?.lng() - lng);
            const rectangleBounds = new google.maps.LatLngBounds(
                new google.maps.LatLng(latLow, lngLow),
                new google.maps.LatLng(latHigh, lngHigh)
            );

            const visibleStore = Object.entries(markers).reduce((storeIndex, [storeId, marker]) => {
                if (rectangleBounds.contains(marker.getPosition())) {
                    storeIndex.push(storeId);
                }

                return storeIndex;
            }, [] as string[]);

            setVisibleStores(visibleStore);
        }
    }, [mapState, idle, markers]);

    useEffect(() => {
        if (!markers || !markerClusterer || !map) return;

        if (map.getZoom() < MAP_CLUSTER_ZOOM) {
            markerClusterer.clearMarkers();
        }

        const markersOnMap = getMarkersOnMap(markers, map);

        Object.entries(markers).forEach(([index, marker]) =>
            marker.setVisible(getShouldShowStore(index))
        );

        markerClusterer.addMarkers(Object.values(markersOnMap));
    }, [showPartnerStores, markers, markerClusterer, map, searchResult, mapBounds]);

    useEffect(() => {
        if (!clientLocation || !markerClusterer || !map) return;

        const position = new google.maps.LatLng(clientLocation?.lat, clientLocation?.lng);

        if (clientMarker) {
            markerClusterer.addMarker(clientMarker);
            map.setValues({
                center: position,
            });
        } else {
            const marker = createMarker({
                icon: storeMarkerIcon(icon.clientPin.src),
                position,
                zIndex: 3,
            });

            markerClusterer.addMarker(marker);

            map.setValues({
                center: position,
            });

            setClientMarker(marker);
        }
        map.setZoom(zoomOnSelect);
    }, [clientMarker, clientLocation]);

    useEffect(() => {
        if (map && searchResult?.length > 0) {
            const { location, viewport } = searchResult[0].geometry;

            const closest = findNearestPoint(location, Object.values(markers));

            const [lt, lng] = closest.nearestPoint ?? [location.lat(), location.lng()];

            const newCenter = new google.maps.LatLng(lt, lng);

            map.setValues({
                center: newCenter,
            });
            const isCountryResult = searchResult?.[0]?.types?.find((type) => type === "country");
            const initialZoom = activeSiteConfig.geo.initZoomLevel ?? 5;
            map.fitBounds(viewport);
            map.setCenter(newCenter);
            if (isCountryResult) {
                map.setZoom(isExtraLargeViewport ? initialZoom : initialZoom - 2);
            }
        }
    }, [searchResult, markers]);

    useEffect(() => {
        if (filterDistance && mapState === MapState.Completed) {
            let center = map.getCenter();
            let zoom;
            if (isNumber(filterDistance.value)) {
                zoom = calculateZoomLevel(filterDistance.value);
            } else {
                zoom =
                    (isExtraLargeViewport
                        ? activeSiteConfig.geo.initZoomLevel
                        : activeSiteConfig.geo.initZoomLevel - 2) ?? 10;
                center = new google.maps.LatLng(
                    activeSiteConfig?.geo?.centerLat,
                    activeSiteConfig?.geo?.centerLng
                );
            }
            map.setCenter(center);
            map.setZoom(zoom);
        }
    }, [filterDistance?.value, filterDistance?.label]);

    useEffect(() => {
        setFilterDistance(null);
    }, [map?.getBounds, map?.getCenter()]);

    const mapRef = useCallback(
        (node: HTMLDivElement) => {
            try {
                if (node === null) {
                    setIdle(0);
                    setMapState(MapState.Unmounted);
                    return;
                }
                const { country, geo } = activeSiteConfig;
                const googleMaps = new GoogleMaps({
                    apiKey: process.env.NEXT_PUBLIC_GOOGLE_MAP_API_KEY,
                    region: country,
                    language: locale,
                    libraries: ["geometry", "places"],
                });

                googleMaps.load().then(() => {
                    map = new google.maps.Map(node, {
                        disableDefaultUI: true,
                        maxZoom: 16,
                        mapId: process.env.NEXT_PUBLIC_GOOGLE_MAP_ID,
                    });

                    map.fitBounds(
                        new google.maps.LatLngBounds(
                            new google.maps.LatLng(geo?.swLat, geo?.swLng),
                            new google.maps.LatLng(geo?.neLat, geo?.neLng)
                        )
                    );

                    const mapControlNode = document.createElement("div");

                    const handleZoom = (level: number) => {
                        setFilterDistance(null);
                        map.setZoom(map.getZoom() + level);
                    };

                    const root = createRoot(mapControlNode);
                    root.render(
                        <Box
                            data-testid="store-locator-map"
                            mr="12px"
                            mb={2}
                            display="flex"
                            flexDirection="row"
                            gap={2}
                        >
                            <IconButton
                                w={space["6.8"]}
                                h={space["6.8"]}
                                aria-label="Zoom Out"
                                icon={
                                    <Image
                                        w="100%"
                                        h="100%"
                                        src={icon.zoomOut.src}
                                        alt="Zoom Out"
                                    />
                                }
                                onClick={() => handleZoom(-1)}
                            />
                            <IconButton
                                w={space["6.8"]}
                                h={space["6.8"]}
                                aria-label="Zoom In"
                                icon={
                                    <Image w="100%" h="100%" src={icon.zoomIn.src} alt="Zoom In" />
                                }
                                onClick={() => handleZoom(1)}
                            />
                        </Box>
                    );

                    map.controls[google.maps.ControlPosition.RIGHT_BOTTOM].push(mapControlNode);

                    setMarkerClusterer(
                        new MarkerClusterer({
                            map,
                            renderer: clusterRenderer,
                            algorithm: clusterAlgorithm,
                        })
                    );

                    map.addListener("idle", () => {
                        setIdle((prevIdle) => {
                            if (prevIdle === 0) {
                                const initialZoom = activeSiteConfig.geo.initZoomLevel ?? 5;
                                isExtraLargeViewport
                                    ? map.setZoom(initialZoom)
                                    : map.setZoom(initialZoom - 2);
                                map.setCenter(
                                    new google.maps.LatLng(geo?.centerLat, geo?.centerLng)
                                );
                            }
                            return Date.now();
                        });
                        setIsLoading(false);
                        setMapBounds(map.getBounds());
                    });
                    map.addListener("dragend", () => {
                        setFilterDistance(null);
                    });
                    map.addListener("click", () => {
                        setFilterDistance(null);
                    });
                });
            } catch (error) {
                Logger.error(ServiceType.WEB, "StoreLocatorMap", {
                    tag: LogTag.STORES,
                    error,
                });
            }
        },
        [locale, setMapBounds]
    );

    useEffect(() => {
        if (idle !== 0) return;
        setMapState(MapState.Mounted);
    }, [idle]);

    return (
        <>
            {!isClickAndCollect && <StoresToggle />}
            <Box
                data-testid={MAP_CONTAINER}
                data-markers-on-map-count={
                    getMarkersOnMap(markers, map).filter((marker) => marker.getVisible()).length
                }
                data-map-ready={mapState === MapState.Completed}
                position="absolute"
                width="100%"
                height="100%"
                ref={mapRef}
            />
        </>
    );
};
